/**
 * HOOK: useProcessingPayload
 *
 * Extracts the processing-payload concern from App.jsx — 5 useState pairs,
 * 3 methods (processParserOutput, applyManualResumenTotales,
 * resetProcessingSession), and 6 derived useMemo values.
 *
 * Flat convivencia-friendly return: all 19 exports are top-level named
 * values so App.jsx can destructure them directly alongside current
 * inline states without renaming.
 *
 * IMPORTANT: The hook receives ALL its data dependencies as read-only
 * parameters. It does NOT import from App.jsx's core/engine/ or utils/.
 */

import { useState, useMemo, useCallback } from 'react'

// ── Internal pure helpers ──────────────────────────────────────────

/**
 * Extract raw resumen candidates from parser output.
 * 7 candidate paths, first-writer wins.
 * @param {object} parserOutput
 * @returns {Map<string, number>}
 */
function extractRawResumenCandidates(parserOutput = {}) {
  const candidates = [
    parserOutput?.resumenHistoriaLaboral?.totalesPorCuit,
    parserOutput?.resumenHistoriaLaboral?.totales_por_cuit,
    parserOutput?.resumenHistoriaLaboral?.empleadores,
    parserOutput?.totalesPorCuit,
    parserOutput?.totales_por_cuit,
    parserOutput?.debug?.totalesPorCuit,
    parserOutput?.etlSummary?.totalesPorCuit,
  ]
  const map = new Map()
  candidates.forEach((candidate) => {
    if (!candidate) return
    if (Array.isArray(candidate)) {
      candidate.forEach((item) => {
        const cuit = String(item?.cuit || item?.cuil || item?.idEmpleador || '').replace(/\D/g, '')
        const total = Number(item?.total ?? item?.monto ?? item?.remuneracionTotal ?? 0)
        if (cuit && total > 0 && !map.has(cuit)) map.set(cuit, total)
      })
      return
    }
    if (typeof candidate === 'object') {
      Object.entries(candidate).forEach(([rawCuit, rawTotal]) => {
        const cuit = String(rawCuit || '').replace(/\D/g, '')
        const total = Number(rawTotal)
        if (cuit && total > 0 && !map.has(cuit)) map.set(cuit, total)
      })
    }
  })
  return map
}

/**
 * Normalize a row's periodoKey to YYYY-MM format.
 * @param {object} row
 * @returns {string}
 */
function normalizePeriodoKey(row) {
  const pk = String(row?.periodoKey || '').trim()
  if (/^\d{4}-\d{2}$/.test(pk)) return pk
  const iso = String(row?.periodoISO || '').trim()
  if (/^\d{4}-\d{2}$/.test(iso)) return iso
  const per = String(row?.periodo || '').trim()
  const slash = per.match(/^(\d{2})\/(\d{4})$/)
  if (slash) return `${slash[2]}-${slash[1]}`
  return ''
}

/** Convert YYYY-MM key to 6-digit number (e.g. 202401). */
function toYmNum(key) {
  const m = String(key || '').match(/^(\d{4})-(\d{2})$/)
  if (!m) return 0
  return Number(`${m[1]}${m[2]}`)
}

/** Convert 6-digit number back to YYYY-MM string. */
function ymNumToKey(ym) {
  const raw = String(ym || '')
  if (!/^\d{6}$/.test(raw)) return ''
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}`
}

// ── Default state values ───────────────────────────────────────────

const DEFAULT_SHADOW_META = { sourceConfidence: 1, qualityWarning: '', duplicateRatio: 0 }

// ── Hook ───────────────────────────────────────────────────────────

/**
 * @param {object} params
 * @param {Array}  params.rows
 * @param {object} params.expediente
 * @param {object} params.CORE_RULES_CONFIG
 * @param {Function} params.computeShadowBilling
 * @param {Function} params.extractResumenTotalesPorCuit
 * @param {Function} params.computeActaHash
 * @param {Function} params.normalizeReviewRows
 * @param {Function} params.sanitizarRemuneraciones
 * @param {Array}  params.activeRules
 * @param {object} params.expedienteCalculo
 * @param {object} params.modoCaso
 */
export function useProcessingPayload({
  rows,
  expediente,
  CORE_RULES_CONFIG,
  computeShadowBilling,
  extractResumenTotalesPorCuit,
  computeActaHash,
  normalizeReviewRows,
  sanitizarRemuneraciones,
  activeRules,
  expedienteCalculo,
  modoCaso,
}) {
  // ── 5 state pairs ────────────────────────────────────────────────
  const [processingPayload, setProcessingPayload] = useState(null)
  const [processingPayloadAt, setProcessingPayloadAt] = useState('')
  const [shadowResumenTotales, setShadowResumenTotales] = useState(new Map())
  const [shadowResumenMeta, setShadowResumenMeta] = useState({ ...DEFAULT_SHADOW_META })
  const [conciliacionManualActa, setConciliacionManualActa] = useState(null)

  // ── processParserOutput ──────────────────────────────────────────
  const processParserOutput = useCallback(
    (data, contextSnapshot = {}) => {
      // Guard: null data
      if (!data) {
        setProcessingPayload(null)
        setProcessingPayloadAt('')
        return { normalized: [], normalizedPre94: [], draftRows: [] }
      }

      // 1. Set payload + timestamp
      setProcessingPayload(data)
      setProcessingPayloadAt(new Date().toISOString())

      // 2. Extract resumen totals + meta
      const resumenTotales = extractResumenTotalesPorCuit(data)
      const resumenRaw = extractRawResumenCandidates(data)
      const resumenMeta = {
        sourceConfidence: Number(data?.resumenHistoriaLaboral?.sourceConfidence || 1),
        qualityWarning: String(data?.resumenHistoriaLaboral?.qualityWarning || ''),
        duplicateRatio: Number(data?.resumenHistoriaLaboral?.duplicateRatio || 0),
      }

      // 3. Set shadow states
      setShadowResumenTotales(resumenTotales)
      setShadowResumenMeta(resumenMeta)
      setConciliacionManualActa(null)

      // 4. Compute draftRows (for App.jsx to set manualResumenTotalesDraft)
      const draftTotalsSrc = resumenRaw.size ? resumenRaw : resumenTotales
      const draftRows = Array.from(draftTotalsSrc.entries()).map(([cuitKey, total]) => ({
        cuit: cuitKey,
        total: String(Number(total || 0)),
        motivo: '',
        evidenciaFuente: '',
      }))

      // 5. Normalize rows
      const {
        rows: ctxRows = rows,
        expediente: ctxExp = expediente,
        activeRules: ctxRules = activeRules,
      } = contextSnapshot

      const ultimo = String(data?.ultimoPeriodoDetectado || '')
      const rawData = Array.isArray(data?.remuneraciones) ? data.remuneraciones : []
      const limpias = sanitizarRemuneraciones(rawData, ctxExp?.fechaCese, ultimo, ctxRules)
      const pre94Rows = Array.isArray(data?.historiaLaboralHasta0694) ? data.historiaLaboralHasta0694 : []
      const normalizedPre94 = normalizeReviewRows(
        pre94Rows.map((r) => ({
          ...r,
          requiresValidation: true,
          requiereRevision: true,
          observacionFecha: r?.observacionFecha || 'Periodo recuperado desde bloque HASTA 06/94',
        }))
      )
      const normalized = normalizeReviewRows([...limpias, ...normalizedPre94])

      return { normalized, normalizedPre94, draftRows }
    },
    [
      rows,
      expediente,
      activeRules,
      extractResumenTotalesPorCuit,
      normalizeReviewRows,
      sanitizarRemuneraciones,
    ]
  )

  // ── applyManualResumenTotales ────────────────────────────────────
  const applyManualResumenTotales = useCallback(
    async (draftRows) => {
      const nextMap = new Map()
      const correcciones = []
      ;(Array.isArray(draftRows) ? draftRows : []).forEach((row) => {
        const cuit = String(row?.cuit || '').replace(/\D/g, '')
        const total = Number(row?.total || 0)
        const motivo = String(row?.motivo || '').trim()
        const evidenciaFuente = String(row?.evidenciaFuente || '').trim()
        if (cuit && total > 0 && motivo && evidenciaFuente) {
          nextMap.set(cuit, total)
          correcciones.push({ cuit, total: Number(total.toFixed(2)), motivo, evidenciaFuente })
        }
      })
      setShadowResumenTotales(nextMap)
      if (!correcciones.length) {
        setConciliacionManualActa(null)
        return
      }
      const payload = {
        version: 1,
        tipo: 'conciliacion_manual_shadow_billing',
        createdAt: new Date().toISOString(),
        sourceConfidence: Number(shadowResumenMeta.sourceConfidence || 0),
        duplicateRatio: Number(shadowResumenMeta.duplicateRatio || 0),
        correcciones,
      }
      const hashIntegridad = await computeActaHash(payload)
      setConciliacionManualActa({ ...payload, hashIntegridad })
    },
    [shadowResumenMeta, computeActaHash]
  )

  // ── resetProcessingSession ───────────────────────────────────────
  const resetProcessingSession = useCallback(() => {
    setProcessingPayload(null)
    setProcessingPayloadAt('')
    setShadowResumenTotales(new Map())
    setShadowResumenMeta({ ...DEFAULT_SHADOW_META })
    setConciliacionManualActa(null)
    // NOTE: manualResumenTotalesDraft is NOT reset here — owned by App.jsx
  }, [])

  // ── 6 derived useMemo values ─────────────────────────────────────

  // #1 — depends on: rows, shadowResumenTotales
  const shadowBilling = useMemo(() => {
    if (!(shadowResumenTotales instanceof Map) || shadowResumenTotales.size === 0) return null
    return computeShadowBilling(
      normalizeReviewRows(rows),
      shadowResumenTotales,
      CORE_RULES_CONFIG?.desbalanceThresholdPct
    )
  }, [rows, shadowResumenTotales])

  // #2 — depends on: rows, shadowBilling
  const scoreCertezaInforme = useMemo(() => {
    if (!CORE_RULES_CONFIG) {
      return { confianzaInforme: 0, factual: 0, inferred: 0, corrupt: 0, total: 0, notaTecnica: '' }
    }
    const weights = CORE_RULES_CONFIG.certaintyWeights || { FACTUAL: 100, INFERRED: 70, CORRUPT: 30 }
    const base = (Array.isArray(rows) ? rows : []).filter((r) => Number(r.monto || 0) > 0)
    if (!base.length) {
      return { confianzaInforme: 0, factual: 0, inferred: 0, corrupt: 0, total: 0, notaTecnica: '' }
    }
    const factual = base.filter((r) => String(r.estadoIdentidad || '') === 'FACTUAL').length
    const inferred = base.filter((r) => String(r.estadoIdentidad || '') === 'INFERRED').length
    const corrupt = base.filter((r) => String(r.estadoIdentidad || '') === 'CORRUPT').length
    const sum = base.reduce((acc, r) => acc + Number(r.confianzaIdentidad || weights.CORRUPT || 30), 0)
    let confianzaInforme = Number((sum / base.length).toFixed(2))
    let notaTecnica = ''
    if (!shadowBilling?.enabled) {
      confianzaInforme = Math.min(confianzaInforme, 80)
      notaTecnica = 'Conciliacion de integridad no verificada por falta de datos de origen'
    }
    return {
      confianzaInforme,
      factual,
      inferred,
      corrupt,
      total: base.length,
      notaTecnica,
    }
  }, [rows, shadowBilling])

  // #3 — depends on: shadowBilling
  const shadowTopConflicts = useMemo(() => {
    if (!shadowBilling?.enabled || !Array.isArray(shadowBilling?.desbalancePorCuit)) return []
    return shadowBilling.desbalancePorCuit
      .filter((r) => r.hasDiscrepancy)
      .slice()
      .sort((a, b) => Number(b.diferenciaPct || 0) - Number(a.diferenciaPct || 0))
      .slice(0, 3)
  }, [shadowBilling])

  // #4 — depends on: processingPayload, rows
  const periodosResumenControl = useMemo(() => {
    const detalle = Array.isArray(processingPayload?.resumenHistoriaLaboralDetalle)
      ? processingPayload.resumenHistoriaLaboralDetalle
      : Array.isArray(processingPayload?.etlSummary?.resumenHistoriaLaboralDetalle)
        ? processingPayload.etlSummary.resumenHistoriaLaboralDetalle
        : []
    if (!detalle.length) {
      return { available: false, total: 0, consistentes: 0, conBrecha: 0, topBrechas: [] }
    }
    const rowsNorm = normalizeReviewRows(rows)
    const byCuit = new Map()
    rowsNorm.forEach((r) => {
      const cuit = String(r.cuit || '').replace(/\D/g, '')
      const key = String(r.periodoKey || '')
      if (!cuit || !key) return
      if (!byCuit.has(cuit)) byCuit.set(cuit, new Set())
      byCuit.get(cuit).add(key)
    })

    const topBrechas = []
    let consistentes = 0
    let conBrecha = 0
    detalle.forEach((d) => {
      const cuit = String(d?.idEmpleador || '').replace(/\D/g, '')
      const desdeKey = normalizePeriodoKey({ periodo: d?.desde })
      const hastaKey = normalizePeriodoKey({ periodo: d?.hasta })
      const desde = toYmNum(desdeKey)
      const hasta = toYmNum(hastaKey)
      if (!cuit || !desde || !hasta || hasta < desde) return
      const monthsExpected = []
      let cursor = desde
      while (cursor <= hasta) {
        const k = ymNumToKey(cursor)
        if (k) monthsExpected.push(k)
        const y = Number(String(cursor).slice(0, 4))
        const m = Number(String(cursor).slice(4, 6))
        cursor = m === 12 ? (y + 1) * 100 + 1 : y * 100 + (m + 1)
      }
      const actual = byCuit.get(cuit) || new Set()
      const missing = monthsExpected.filter((m) => !actual.has(m))
      if (!missing.length) {
        consistentes += 1
        return
      }
      conBrecha += 1
      topBrechas.push({
        cuit,
        razonSocial: String(d?.razonSocialNormalizada || d?.razonSocialOriginal || 'SIN_DATO'),
        missingCount: missing.length,
      })
    })

    return {
      available: true,
      total: detalle.length,
      consistentes,
      conBrecha,
      topBrechas: topBrechas.sort((a, b) => b.missingCount - a.missingCount).slice(0, 3),
    }
  }, [processingPayload, rows])

  // #5 — depends on: processingPayload
  const resumenHistorialLaboralRows = useMemo(() => {
    const detalle = Array.isArray(processingPayload?.resumenHistoriaLaboralDetalle)
      ? processingPayload.resumenHistoriaLaboralDetalle
      : Array.isArray(processingPayload?.etlSummary?.resumenHistoriaLaboralDetalle)
        ? processingPayload.etlSummary.resumenHistoriaLaboralDetalle
        : []
    return detalle
      .map((d, idx) => ({
        key: `${String(d?.idEmpleador || 'sin-id')}-${String(d?.desde || '')}-${String(d?.hasta || '')}-${idx}`,
        razonSocial: String(d?.razonSocialNormalizada || d?.razonSocialOriginal || 'SIN_DATO'),
        identificador: String(d?.idEmpleador || ''),
        tipoId: String(d?.tipoId || 'SIN_ID'),
        desde: String(d?.desde || ''),
        hasta: String(d?.hasta || ''),
      }))
      .filter((r) => r.desde && r.hasta)
  }, [processingPayload])

  // #6 — depends on: processingPayload
  const simulationFlowActive = useMemo(() => Boolean(processingPayload?.simulationMeta), [processingPayload])

  // ── Return flat object ───────────────────────────────────────────
  return {
    // 5 state pairs
    processingPayload,
    setProcessingPayload,
    processingPayloadAt,
    setProcessingPayloadAt,
    shadowResumenTotales,
    setShadowResumenTotales,
    shadowResumenMeta,
    setShadowResumenMeta,
    conciliacionManualActa,
    setConciliacionManualActa,

    // 3 methods
    processParserOutput,
    applyManualResumenTotales,
    resetProcessingSession,

    // 6 derived values
    shadowBilling,
    scoreCertezaInforme,
    shadowTopConflicts,
    periodosResumenControl,
    resumenHistorialLaboralRows,
    simulationFlowActive,
  }
}
